import axios, { AxiosError } from 'axios';
import loadImage from 'blueimp-load-image';
import { useState } from 'react';

import { postPresingedURL } from '../../../api/photo';
import { ErrorState } from '../../../type';
import { cacheImagesToCloudFront, IMAGE_EXTENSIONS_ARRAY } from '../../common';
import { useAlertModal } from '../hook';

interface UseHandleFileChange {
  /** 로딩의 진행 수치 ex) min:0 ~ max:100 */
  progress: number;

  /** 로딩 중인지 아닌지 */
  isLoading: boolean;

  /** 로딩 중인 파일의 개수 */
  loadingFileCount: number;

  handleFileChange: (
    e: React.ChangeEvent<HTMLInputElement>,

    /** 최대로 저장할 수 있는 파일의 개수 */
    maxPhotoLength?: number,
  ) => Promise<void>;
}

export const useHandleFileChange = (
  onSuccess: (photoURLs: string[]) => void,
): UseHandleFileChange => {
  const { openAlertModal } = useAlertModal();

  const [fileUploadPercentage, setFileUploadPercentage] = useState<number[]>();
  const [isLoading, setIsLoading] = useState(false);

  const progress =
    (fileUploadPercentage?.reduce((a, b) => a + b, 0) ?? 0) /
    (fileUploadPercentage ? fileUploadPercentage.length : 0);

  const loadingFileCount = isLoading ? (fileUploadPercentage?.length ?? 0) : 0;

  const myUploadProgress =
    (index: number) =>
    ({ loaded, total }: { loaded: number; total: number }) => {
      const percentage = Math.floor((loaded * 100) / total);

      setFileUploadPercentage((prev) => {
        if (!prev) return;
        const newArr = [...prev];
        newArr[index] = percentage;
        return newArr;
      });
    };

  const handleOpenUnsupportedFileModal = () => {
    openAlertModal({
      content: (
        <>
          지원하지 않는 이미지 형식입니다. 다른 이미지를 업로드해주세요.
          <p className="text-14px leading-5 text-gray-600">
            *업로드가 가능한 이미지 형식 : png / gif / jpeg / svg / webp / bmp
          </p>
        </>
      ),
    });
  };

  /**
   * 이미지의 EXIF orientation 정보를 기반으로 올바른 방향으로 회전시킨 파일을 반환
   *
   * @param file - 방향을 수정할 이미지 파일
   * @returns 방향이 수정된 File 객체
   * @throws 이미지 로드 또는 변환 실패 시 Error 발생
   */
  const fixImageOrientation = (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      loadImage(
        file,
        (img) => {
          if (!(img instanceof HTMLCanvasElement)) {
            reject(new Error('이미지를 캔버스로 로드하는데 실패했습니다'));
            return;
          }

          img.toBlob((blob) => {
            if (!blob) {
              reject(new Error('캔버스를 Blob으로 변환하는데 실패했습니다'));
              return;
            }

            const fixedFile = new File([blob], file.name, {
              type: file.type,
            });
            resolve(fixedFile);
          }, file.type);
        },
        {
          orientation: true,
          canvas: true,
        },
      );
    });
  };

  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    maxPhotoLength?: number,
  ) => {
    try {
      if (!e.target.validity.valid) {
        throw new Error('지원하지 않는 이미지 형식입니다.');
      }

      setIsLoading(true);

      const { files } = e.target;

      if (files === null) throw new Error('파일이 없습니다.');

      const fileArray = maxPhotoLength
        ? Array.from(files).slice(0, maxPhotoLength)
        : Array.from(files);

      const fileTypes = fileArray.map((file) => file.type);

      const isUnsupportedFile = fileTypes.some(
        (f) => !IMAGE_EXTENSIONS_ARRAY.includes(f),
      );

      if (isUnsupportedFile) {
        return handleOpenUnsupportedFileModal();
      }

      // 이미지 방향 수정 (실패 시 원본 파일 사용)
      const fixedFiles = await Promise.all(
        fileArray.map(async (file) => {
          try {
            return await fixImageOrientation(file);
          } catch (error) {
            console.warn('이미지 방향 수정 실패, 원본 파일 사용:', error);
            return file;
          }
        }),
      );

      // 사진 개수만큼 0 배열 추가
      setFileUploadPercentage(fixedFiles.map(() => 0));

      try {
        const presignedData = await postPresingedURL(fileTypes);

        const results = await Promise.all(
          fixedFiles.map((file, index) => {
            const { presigned } = presignedData.data[index];
            const formData = new FormData();
            for (const key in presigned.fields) {
              formData.append(
                key,
                presigned.fields[key as keyof typeof presigned.fields],
              );
            }
            formData.append('Content-Type', file.type);
            formData.append('file', file);

            return axios.post(presigned.url, formData, {
              onUploadProgress: myUploadProgress(index),
            });
          }),
        );

        const isUploadSucceed = results.some((r) => r.status !== 204) === false;

        const photoURLs = presignedData.data.map(
          ({
            presigned: {
              url,
              fields: { key },
            },
          }) => `${url}${key}`,
        );

        if (isUploadSucceed) {
          onSuccess(photoURLs);
          cacheImagesToCloudFront(photoURLs);
        }
      } catch (error) {
        const typedError = error as AxiosError<ErrorState>;

        if (typedError?.response?.data?.message[0]?.includes('fileTypes')) {
          return handleOpenUnsupportedFileModal();
        }

        throw error;
      }
    } catch (error) {
      console.error(error);
      if (error instanceof Error) {
        openAlertModal(error.message);
      } else {
        openAlertModal('예상치 못한 오류가 발생했습니다.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return { progress, isLoading, loadingFileCount, handleFileChange };
};
